import React, { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { canUpgradeToPaid, isPaidPlan } from "../lib/billing";

import { AppVersionText } from "../components/AppVersionText";
import { AppHeaderFrame } from "../components/AppHeaderFrame";
import { KnowledgeAutoRegistrationSettings } from "../components/KnowledgeAutoRegistrationSettings";
import { ReportButton } from "../components/ReportButton";
import { ReLoginMethodNotice } from "../components/ReLoginMethodNotice";
import { useReportCenter } from "../contexts/ReportCenterContext";
import { useAccountState } from "../hooks/useAccountState";
import { useHomeData } from "../hooks/useHomeData";
import { useLevelUpCelebration } from "../hooks/useLevelUpCelebration";
import {
  api,
  type AccountState,
  type FeatureKey,
  type PrivilegeApplyErrorCode,
} from "../lib/api";
import { getCharacterShortName } from "../lib/characterName";
import { AppError, toUserMessage } from "../lib/errors";
import {
  canUseAnyFeature,
  canUseFeature,
  FEATURE_LOCK_MESSAGE,
  resolveFeatureAccessUiState,
  type FeatureAccessUiState,
} from "../lib/featureAccess";
import {
  GOOGLE_SCOPE_BUNDLES,
  getGoogleIntegrationAggregateStatusLabel,
  resolveGoogleIntegrationAggregateState,
} from "../lib/googleScopes";
import {
  getLineLinkStatusLabel,
  resolveLineLinkSnapshotFromAccountState,
} from "../lib/lineLink";
import { logoutToSplash } from "../lib/logout";
import { storage } from "../lib/storage";

const AI_EXECUTIVE_OFFICE_GREETING =
  "代表、お疲れさまです！\n相談したいことも、頼みたいことも、ここから一緒に進めましょう。";
const LEGACY_HOME_GREETING =
  "代表、お疲れさまです！少し一息入れませんか。こちらはいつでも動けるように準備しています！！";
const REQUEST_FEATURES: FeatureKey[] = [
  "news",
  "report",
  "company_research",
];

const FEATURE_USAGE_ROWS: Array<{ feature: FeatureKey; limitKey: string; label: string }> = [
  { feature: "chat", limitKey: "monthly_messages", label: "チャット" },
  { feature: "group_talk", limitKey: "monthly_turns", label: "AI壁打ちルーム（ターン）" },
  { feature: "news", limitKey: "manual_runs_monthly", label: "ニュース" },
  { feature: "report", limitKey: "manual_runs_monthly", label: "深掘りレポート" },
  { feature: "company_research", limitKey: "deep_dive_monthly", label: "企業リサーチ" },
  { feature: "receipt_ocr", limitKey: "monthly_receipts", label: "領収書整理" },
  { feature: "memo_ocr", limitKey: "monthly_images", label: "社長メモ" },
];

const PLAN_LABELS: Record<string, string> = {
  none: "未設定",
  free: "Free",
  invite_trial: "特典コード適用中",
  paid: "Standard",
  internal: "Internal",
};

const privilegeErrorMessages: Record<PrivilegeApplyErrorCode, string> = {
  invalid_privilege_code: "特典コードが見つかりません。入力内容を確認してください。",
};

function isPrivilegeApplyErrorCode(value: unknown): value is PrivilegeApplyErrorCode {
  return value === "invalid_privilege_code";
}

function extractPrivilegeApplyErrorCode(error: unknown): PrivilegeApplyErrorCode | null {
  if (!(error instanceof AppError) || !error.meta.responseText) return null;

  try {
    const parsed = JSON.parse(error.meta.responseText) as {
      errorCode?: unknown;
      error_code?: unknown;
      data?: { errorCode?: unknown; error_code?: unknown };
    };
    const errorCode = parsed.errorCode ?? parsed.error_code ?? parsed.data?.errorCode ?? parsed.data?.error_code;
    return isPrivilegeApplyErrorCode(errorCode) ? errorCode : null;
  } catch {
    return null;
  }
}

function resolveHomeGreeting(messageToUser: string) {
  const trimmed = messageToUser.trim();
  if (!trimmed || trimmed === LEGACY_HOME_GREETING) {
    return AI_EXECUTIVE_OFFICE_GREETING;
  }
  return messageToUser;
}

type MenuButtonProps = {
  title: string;
  description?: string;
  status?: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick?: () => void;
};

function MenuButton({ title, description, status, disabled, destructive, onClick }: MenuButtonProps) {
  const resolvedTitle = title === "プロフィール設定" ? "プロファイル設定" : title;
  const resolvedDescription =
    title === "プロフィール設定" ? "あなたの情報を設定できます。" : description;

  return (
    <button
      type="button"
      aria-label={resolvedTitle}
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-start rounded-[1.1rem] px-4 py-3 text-left transition ${
        disabled ? "cursor-not-allowed opacity-55" : "hover:bg-[#FDF8F5]"
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className={`text-sm font-bold ${destructive ? "text-[#A1493E]" : "text-[#5D4D46]"}`}>{resolvedTitle}</span>
          {status ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                disabled
                  ? "border border-[#E7DDD6] bg-[#F7F2EE] text-[#A1887D]"
                  : "border border-[#E6DACD] bg-[#FFF8F1] text-[#A46D4C]"
              }`}
            >
              {status}
            </span>
          ) : null}
        </span>
        {resolvedDescription ? (
          <span className="mt-1 block text-xs font-medium leading-5 text-[#7B6861]">{resolvedDescription}</span>
        ) : null}
      </span>
    </button>
  );
}

type MenuSectionId = "usage" | "integrations" | "policies" | "other";

type MenuRootButtonProps = {
  title: string;
  hasChildren?: boolean;
  onClick: () => void;
};

function MenuRootButton({ title, hasChildren = true, onClick }: MenuRootButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-[1.1rem] px-4 py-3 text-left transition hover:bg-[#FDF8F5]"
    >
      <span className="text-sm font-bold text-[#5D4D46]">{title}</span>
      {hasChildren ? (
        <span className="text-sm font-black text-[#A67C52]" aria-hidden="true">
          →
        </span>
      ) : null}
    </button>
  );
}

type HomeActionCardProps = {
  to: string;
  label: string;
  title: string;
  description: string;
  accessState: FeatureAccessUiState;
};

function HomeActionCard({ to, label, title, description, accessState }: HomeActionCardProps) {
  const locked = accessState === "locked";
  const checking = accessState === "checking";
  const baseClass =
    "relative min-h-[10.5rem] rounded-[1.8rem] border border-[#D9C5B9] bg-white/85 px-3 py-5 text-left text-[#5D4D46] shadow-[0_10px_24px_rgba(124,90,78,0.10)] transition-all";
  const lockedClass = "border border-[#E0D5CE] bg-[#F4EEE9] text-[#8A756C] opacity-85";
  const availableClass = "active:scale-[0.98] hover:-translate-y-0.5 hover:bg-white";
  const className = `${baseClass} ${locked ? lockedClass : checking ? "cursor-default" : availableClass}`;

  const content = (
    <>
      {locked ? (
        <span className="absolute right-3 top-3 rounded-full bg-white px-2 py-1 text-[10px] font-black text-[#A1887D]">
          ロック中
        </span>
      ) : null}
      <span className="block text-[10px] font-black tracking-[0.08em] text-[#A67C52]">{label}</span>
      <span className="mt-2 block text-base font-black tracking-wide sm:text-lg">{title}</span>
      <span className="mt-2 block text-xs font-medium leading-5 text-[#8B7065]">
        {locked ? FEATURE_LOCK_MESSAGE : description}
      </span>
    </>
  );

  if (locked || checking) {
    return (
      <button
        type="button"
        className={className}
        aria-disabled="true"
        aria-busy={checking ? "true" : undefined}
        disabled
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  );
}

type HomeWorkActionProps = {
  title: string;
  accessState?: FeatureAccessUiState;
  onClick: () => void;
};

function HomeWorkAction({ title, accessState = "available", onClick }: HomeWorkActionProps) {
  const locked = accessState === "locked";
  const checking = accessState === "checking";
  const disabled = locked || checking;
  return (
    <button
      type="button"
      aria-label={`${title}を開く`}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled ? "true" : undefined}
      aria-busy={checking ? "true" : undefined}
      disabled={disabled}
      className={`flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border px-4 py-2 text-left transition ${
        locked
          ? "cursor-not-allowed border-[#E0D5CE] bg-[#F4EEE9] opacity-85"
          : checking
            ? "cursor-default border-[#EADBC8] bg-white/70"
            : "border-[#EADBC8] bg-white/70 hover:bg-white"
      }`}
    >
      <span className="min-w-0 text-sm font-black text-[#5D4D46]">{title}</span>
      <span className="shrink-0 text-sm font-black text-[#A67C52]" aria-hidden="true">
        {locked ? "ロック中" : "→"}
      </span>
    </button>
  );
}

type UsageRow = {
  label: string;
  limit: number | null;
  used: number;
  remaining: number | null;
  isUnlimited: boolean;
};

function buildUsageRows(accountState: AccountState | null): UsageRow[] {
  const featureUsage = accountState?.featureUsage ?? {};

  return FEATURE_USAGE_ROWS.flatMap(({ feature, limitKey, label }): UsageRow[] => {
    const counter = featureUsage[feature]?.[limitKey];
    if (!counter) return [];

    if (counter.isUnlimited) {
      return [{ label, limit: null, used: Math.max(0, Math.floor(counter.used)), remaining: null, isUnlimited: true }];
    }

    return [
      {
        label,
        limit: Math.max(0, Math.floor(counter.limit ?? 0)),
        used: Math.max(0, Math.floor(counter.used)),
        remaining: Math.max(0, Math.floor(counter.remaining ?? 0)),
        isUnlimited: false,
      },
    ];
  });
}

function usageBarWidth(row: UsageRow): string {
  if (row.isUnlimited) return "100%";
  if (row.limit === null || row.limit <= 0) return "100%";
  const percent = Math.min(100, Math.max(0, (row.used / row.limit) * 100));
  return `${percent}%`;
}

function formatEntitlementExpiresAt(value: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(date);
}

function usageStatus(row: UsageRow): "ok" | "low" | "empty" {
  if (row.isUnlimited) return "ok";
  if (row.limit === null || row.limit <= 0 || (row.remaining ?? 0) <= 0) return "empty";
  if ((row.remaining ?? 0) <= Math.max(1, Math.ceil(row.limit * 0.2))) return "low";
  return "ok";
}

function usageStatusLabel(row: UsageRow): string {
  if (row.isUnlimited) return "無制限";
  if (row.limit === null || row.limit <= 0) return "利用不可";
  if ((row.remaining ?? 0) <= 0) return "上限";
  return `残り ${row.remaining} / ${row.limit}`;
}

function UsageSummary({
  accountState,
  rows,
  titleId,
}: {
  accountState: AccountState;
  rows: UsageRow[];
  titleId?: string;
}) {
  const planLabel = PLAN_LABELS[accountState.plan] ?? accountState.plan;
  // 自動更新(サブスクリプション)の期限は「請求期間の終了日」であり失効日ではないため表示しない。
  // 特典コード・協力者コードなど期限付き付与のみ、いつまで使えるかを案内する。
  const expiresAtLabel = accountState.entitlementAutoRenews
    ? null
    : formatEntitlementExpiresAt(accountState.entitlementExpiresAt);

  return (
    <section className="mb-8 w-full rounded-[1.8rem] border border-[#EADBC8] bg-white/75 px-5 py-5 shadow-[0_12px_30px_rgba(124,90,78,0.08)]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#A67C52]">USAGE</p>
          <h2 id={titleId} className="mt-1 text-base font-black text-[#5D4D46]">
            今月の利用状況
          </h2>
        </div>
        <div className="shrink-0 text-right">
          <span className="inline-block rounded-full border border-[#E6DACD] bg-[#FFF8F1] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
            {planLabel}
          </span>
          {expiresAtLabel ? (
            <p className="mt-1 text-[11px] font-bold text-[#A67C52]">{expiresAtLabel} まで</p>
          ) : null}
        </div>
      </div>

      {rows.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {rows.map((row) => {
            const status = usageStatus(row);
            const barClass =
              status === "empty" ? "bg-[#B65A4D]" : status === "low" ? "bg-[#D39A45]" : "bg-[#6E9B73]";

            return (
              <li key={`${row.label}-${row.limit}`} className="border-t border-[#F0E4DA] pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 text-sm font-black text-[#5D4D46]">{row.label}</span>
                  <span className="shrink-0 text-xs font-black text-[#A67C52]">{usageStatusLabel(row)}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#F1E8E0]">
                  <div className={`h-full rounded-full ${barClass}`} style={{ width: usageBarWidth(row) }} />
                </div>
                <p className="mt-1 text-[11px] font-medium text-[#8B7065]">使用済み {row.used}</p>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-4 rounded-xl bg-[#FFF8F1] px-3 py-3 text-xs font-bold leading-5 text-[#8B7065]">
          利用状況はまだありません。
        </p>
      )}
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    vm,
    currentCharacterId,
    messageToUser,
    lineLinkSnapshot,
    googleIntegrationViews,
    refresh,
  } = useHomeData();
  const { unseenCount, isOpen: isReportDrawerOpen, openReports, setCharacterLastName } = useReportCenter();
  const {
    accountState,
    loading: accountStateLoading,
    error: accountStateError,
    reload: reloadAccountState,
  } = useAccountState();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState<MenuSectionId | null>(null);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [isKnowledgeSettingsOpen, setIsKnowledgeSettingsOpen] = useState(false);

  const [isPrivilegeModalOpen, setIsPrivilegeModalOpen] = useState(false);
  const [privilegeCode, setPrivilegeCode] = useState("");
  const [privilegeApplyLoading, setPrivilegeApplyLoading] = useState(false);
  const [privilegeApplyError, setPrivilegeApplyError] = useState<string | null>(null);
  const [privilegeApplySuccess, setPrivilegeApplySuccess] = useState(false);

  const characterLastName = getCharacterShortName(vm?.characterName);
  const shouldShowUpgrade = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false) && canUpgradeToPaid(accountState);
  const shouldShowBillingPortal = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false) && isPaidPlan(accountState);
  const accountUserId = storage.getAuth().userId ?? null;
  const chatAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseFeature(accountState, "chat")
  );
  const groupTalkAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseFeature(accountState, "group_talk")
  );
  const researchAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseAnyFeature(accountState, REQUEST_FEATURES)
  );
  const knowledgeAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    accountState?.entitlementStatus === "active"
  );
  const memoAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseFeature(accountState, "memo_ocr")
  );
  const receiptAccessState = resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseFeature(accountState, "receipt_ocr")
  );
  const usageRows = useMemo(() => buildUsageRows(accountState), [accountState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setCharacterLastName(characterLastName);
  }, [characterLastName, setCharacterLastName]);

  const closeMenu = () => {
    setActiveMenuSection(null);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
      return;
    }
    setActiveMenuSection(null);
    setIsMenuOpen(true);
  };

  const openPrivilegeModal = useCallback(() => {
    setPrivilegeCode("");
    setPrivilegeApplyError(null);
    setPrivilegeApplySuccess(false);
    setIsPrivilegeModalOpen(true);
  }, []);

  const closePrivilegeModal = useCallback(() => {
    if (privilegeApplyLoading) return;
    setIsPrivilegeModalOpen(false);
    setPrivilegeCode("");
    setPrivilegeApplyError(null);
    setPrivilegeApplySuccess(false);
  }, [privilegeApplyLoading]);

  const openPrivilegeModalFromMenu = useCallback(() => {
    closeMenu();
    openPrivilegeModal();
  }, [openPrivilegeModal]);

  const openUsageModalFromMenu = useCallback(() => {
    closeMenu();
    setIsUsageModalOpen(true);
  }, []);

  const closeUsageModal = useCallback(() => {
    setIsUsageModalOpen(false);
  }, []);

  const openKnowledgeSettingsFromMenu = () => {
    closeMenu();
    setIsKnowledgeSettingsOpen(true);
  };

  const handlePrivilegeApply = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (privilegeApplyLoading) return;
      if (accountState?.authStatus !== "authenticated") {
        setPrivilegeApplyError("ログイン後に特典コードを適用してください。");
        return;
      }

      const normalizedCode = privilegeCode.trim();
      if (!normalizedCode) {
        setPrivilegeApplyError("特典コードを入力してください。");
        return;
      }

      setPrivilegeApplyLoading(true);
      setPrivilegeApplyError(null);

      try {
        const result = await api.applyPrivilegeCode(normalizedCode);
        await reloadAccountState();
        setPrivilegeCode("");
        setPrivilegeApplyError(null);

        if (result.alreadyApplied) {
          setPrivilegeApplyError("この特典コードはすでに適用済みです。");
        } else {
          setPrivilegeApplySuccess(true);
        }
      } catch (errorValue) {
        const errorCode = extractPrivilegeApplyErrorCode(errorValue);
        setPrivilegeApplyError(errorCode ? privilegeErrorMessages[errorCode] : toUserMessage(errorValue));
      } finally {
        setPrivilegeApplyLoading(false);
      }
    },
    [accountState?.authStatus, privilegeApplyLoading, privilegeCode, reloadAccountState]
  );

  const openCharacterSettings = () => {
    closeMenu();
    navigate("/character-switch");
  };

  const openProfileSettings = () => {
    closeMenu();
    navigate("/profile", { state: { returnTo: "/home" } });
  };

  const openLoginMethods = () => {
    closeMenu();
    navigate("/profile/login-methods", { state: { returnTo: "/home" } });
  };

  const openLineLink = () => {
    closeMenu();
    navigate("/line-link", { state: { returnTo: "/home" } });
  };

  const openGoogleLink = () => {
    closeMenu();
    navigate(`/google-link?returnTo=${encodeURIComponent("/home")}&mode=selector`, {
      state: { returnTo: "/home", mode: "selector" },
    });
  };

  const openTermsPage = () => {
    closeMenu();
    navigate("/terms");
  };

  const openPrivacyPolicy = () => {
    closeMenu();
    navigate("/privacy");
  };

  const openCommercialTransactions = () => {
    closeMenu();
    navigate("/commercial-transactions");
  };

  const openSupportInquiry = () => {
    closeMenu();
    navigate("/support-inquiry", { state: { returnTo: "/home" } });
  };

  const openPlansPage = () => {
    closeMenu();
    navigate("/plans", { state: { returnTo: "/home" } });
  };

  const handleLogout = () => {
    logoutToSplash();
  };

  const accountLineLinkSnapshot = useMemo(
    () => resolveLineLinkSnapshotFromAccountState(accountState, lineLinkSnapshot),
    [accountState, lineLinkSnapshot]
  );
  const lineLinkStatusLabel = getLineLinkStatusLabel(accountLineLinkSnapshot);
  const googleAggregateStatus = resolveGoogleIntegrationAggregateState({
    [GOOGLE_SCOPE_BUNDLES.bundle]: false,
    [GOOGLE_SCOPE_BUNDLES.calendarToday]:
      googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.calendarToday].state === "connected",
  });
  const googleLinkStatusLabel = getGoogleIntegrationAggregateStatusLabel(googleAggregateStatus);

  useLevelUpCelebration(vm?.rankName, currentCharacterId, "home");

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased">
        <AppHeaderFrame maxWidthClassName="max-w-md">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>
            <p className="mt-0.5 text-[11px] font-bold tracking-[0.1em] text-[#A67C52]">AI EXECUTIVE OFFICE</p>
          </div>

          <div className="flex items-center gap-2">
            <ReportButton unseenCount={unseenCount} isOpen={isReportDrawerOpen} onClick={openReports} />

            <div className="relative">
              <button
                type="button"
                onClick={toggleMenu}
                aria-label="メニュー"
                aria-expanded={isMenuOpen}
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EADBC8] bg-white/70 shadow-sm"
              >
                <span className="sr-only">メニュー</span>
                <span className="flex flex-col items-center justify-center gap-1">
                  <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                  <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                  <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                </span>
              </button>

              {isMenuOpen ? (
                <>
                <button
                  type="button"
                  aria-label="メニューを閉じる"
                  className="fixed inset-0 z-10 cursor-default bg-transparent"
                  onClick={closeMenu}
                />

                <div className="absolute right-0 top-14 z-20 max-h-[min(78vh,34rem)] w-72 overflow-y-auto rounded-[1.6rem] border border-[#EADBC8] bg-white/95 p-2 shadow-[0_18px_40px_rgba(124,90,78,0.18)] backdrop-blur-sm">
                  {activeMenuSection ? (
                    <>
                      <div className="mb-1 px-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setActiveMenuSection(null)}
                          className="rounded-full px-2 py-1 text-xs font-black text-[#A67C52] transition hover:bg-[#FDF8F5] hover:text-[#7C5A4E]"
                        >
                          ←戻る
                        </button>
                        <p className="mt-1 px-2 text-sm font-black text-[#5D4D46]">
                          {activeMenuSection === "usage"
                            ? "利用状況"
                            : activeMenuSection === "integrations"
                            ? "外部連携"
                            : activeMenuSection === "policies"
                            ? "規約・ポリシー"
                            : "その他"}
                        </p>
                      </div>

                      {activeMenuSection === "usage" ? (
                        <>
                          {accountState?.authStatus === "authenticated" ? (
                            <MenuButton
                              title="利用状況"
                              description="利用回数を確認します。プラン変更もこちら。"
                              onClick={openUsageModalFromMenu}
                            />
                          ) : null}
                          {shouldShowUpgrade ? (
                            <MenuButton
                              title="プランを管理"
                              description="料金とプラン内容を確認してから変更できます。"
                              onClick={openPlansPage}
                            />
                          ) : null}
                          {shouldShowBillingPortal ? (
                            <MenuButton
                              title="プランを管理"
                              description="料金とプラン内容を確認してから変更できます。"
                              onClick={openPlansPage}
                            />
                          ) : null}
                          {accountState?.authStatus === "authenticated" ? (
                            <MenuButton
                              title="特典コードを入力"
                              description="追加特典の受け取りはこちら。"
                              onClick={openPrivilegeModalFromMenu}
                            />
                          ) : null}
                        </>
                      ) : null}

                      {activeMenuSection === "integrations" ? (
                        <>
                          <MenuButton
                            title="外部連携設定"
                            description="カレンダー連携に使います。"
                            status={googleLinkStatusLabel}
                            onClick={openGoogleLink}
                          />
                          <MenuButton
                            title="LINE連携"
                            description="LINEでニュースを受け取ることができます。"
                            status={lineLinkStatusLabel}
                            onClick={openLineLink}
                          />
                        </>
                      ) : null}

                      {activeMenuSection === "policies" ? (
                        <>
                          <MenuButton title="利用規約" onClick={openTermsPage} />
                          <MenuButton title="プライバシーポリシー" onClick={openPrivacyPolicy} />
                          <MenuButton title="特定商取引法に基づく表記" onClick={openCommercialTransactions} />
                        </>
                      ) : null}

                      {activeMenuSection === "other" ? (
                        <>
                          {accountUserId ? (
                            <div className="mx-3 mb-2 rounded-[1rem] border border-[#EADBC8] bg-[#FDF8F5] px-3 py-2">
                              <p className="text-[10px] font-black tracking-[0.08em] text-[#A67C52]">アカウント</p>
                              <p className="mt-1 text-[11px] font-medium leading-5 text-[#7B6861]">
                                お問い合わせ時にお伝えください
                              </p>
                              <p className="mt-1 break-all font-mono text-[11px] font-bold text-[#5D4D46]">
                                ユーザーID: <span className="select-all">{accountUserId}</span>
                              </p>
                            </div>
                          ) : null}
                          <MenuButton
                            title="ナレッジ設定"
                            description="社長メモと企業リサーチの分析結果の自動追加を管理します。"
                            onClick={openKnowledgeSettingsFromMenu}
                          />
                          <MenuButton
                            title="お問い合わせ"
                            description="ログイン・アカウントに関するお困りごとはこちらから。"
                            onClick={openSupportInquiry}
                          />
                          <MenuButton
                            title="ログアウト"
                            description="ログイン方法を選び直します。"
                            onClick={handleLogout}
                          />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="px-4 pb-1 pt-2 text-xs font-black tracking-[0.08em] text-[#A67C52]">WizMate設定</p>
                      <MenuRootButton title="利用状況" onClick={() => setActiveMenuSection("usage")} />
                      <MenuRootButton title="プロファイル設定" hasChildren={false} onClick={openProfileSettings} />
                      <MenuRootButton title="ログイン方法" hasChildren={false} onClick={openLoginMethods} />
                      <MenuRootButton title="キャラクター選択" hasChildren={false} onClick={openCharacterSettings} />
                      <MenuRootButton title="外部連携" onClick={() => setActiveMenuSection("integrations")} />
                      <MenuRootButton title="規約・ポリシー" onClick={() => setActiveMenuSection("policies")} />
                      <MenuRootButton title="その他" onClick={() => setActiveMenuSection("other")} />
                    </>
                  )}

                  <AppVersionText className="px-4 pb-1 pt-2 text-center text-[10px] font-medium text-[#C4AFA8]" />
                </div>
                </>
              ) : null}
            </div>
          </div>
        </AppHeaderFrame>

      <div className="mx-auto min-h-screen w-full max-w-md px-6 pb-6 pt-6 sm:px-8">

        <main className="flex flex-1 flex-col items-center">
          {loading ? <p role="status" className="sr-only">読み込み中です...</p> : null}
          {!loading && error ? <p className="mt-3 text-center text-xs text-[#AF3B2E]">{error}</p> : null}
          {accountStateLoading ? <p role="status" className="sr-only">利用状態を確認しています...</p> : null}
          {!accountStateLoading && accountStateError ? (
            <p className="mt-1 text-center text-xs text-[#AF3B2E]">{accountStateError}</p>
          ) : null}

          {!accountStateLoading && accountState?.entitlementStatus === "expired" ? (
            <div className="mt-4 w-full rounded-[1.6rem] border border-[#E6DACD] bg-[#FFF8F1] px-5 py-5 text-[#4A3E39]">
              <p className="text-sm font-black text-[#A46D4C]">このアカウントでは、有効なプランが確認できませんでした。</p>
              <p className="mt-3 text-xs leading-6 text-[#6B5851]">
                登録済みなのに利用できない場合、別のログイン方法で入っている可能性があります。
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <ReLoginMethodNotice onLogout={handleLogout} />
                <p className="mt-1 text-center text-xs text-[#A67C52]">（問い合わせ先は後日公開予定です）</p>
              </div>
            </div>
          ) : null}

          {vm ? (
            <>
              <section data-testid="home-character-section" className="relative mb-6 mt-12 flex w-full flex-col items-center">
                <div className="absolute -right-4 -top-8 h-36 w-36 rounded-full bg-[#F4E3D7] opacity-60 blur-3xl" />

                <div className="relative h-64 w-64 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)] animate-[bounce_5s_infinite]">
                  <img
                    src={vm.imageUrl || "/img/no-image.png"}
                    alt={vm.characterName || "WizMate character"}
                    onError={(event) => {
                      const target = event.currentTarget;
                      if (!target.src.endsWith("/img/no-image.png")) {
                        target.src = "/img/no-image.png";
                      }
                    }}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="relative -mt-4 w-full max-w-[18rem] rounded-[1.8rem] border border-white/90 bg-white px-5 py-4 text-center shadow-[0_16px_30px_rgba(166,124,82,0.15)]">
                  <span className="absolute left-10 top-[-10px] h-5 w-5 rotate-45 border-l border-t border-white/90 bg-white" />
                  <p className="whitespace-pre-line text-[15px] font-bold leading-7 text-[#5D4D46]">
                    {resolveHomeGreeting(messageToUser)}
                  </p>
                </div>
              </section>

              <section aria-label="仕事を始める" className="mb-6 grid w-full grid-cols-2 gap-3">
                <HomeActionCard
                  to="/chat"
                  label="通常会話"
                  title={`${characterLastName}と話す`}
                  description="日々の相談、ちょっとした調べもの、予定確認を一緒に。"
                  accessState={chatAccessState}
                />

                <HomeActionCard
                  to="/talk/group"
                  label="AI壁打ちルーム"
                  title="AIと壁打ち"
                  description="複数のAIメンバーと、経営や企画をいろいろな視点から考える。"
                  accessState={groupTalkAccessState}
                />

                <HomeActionCard
                  to="/request"
                  label="リサーチ・情報収集"
                  title="調べてもらう"
                  description="企業リサーチ、ニュース、レポートをまとめて任せる。"
                  accessState={researchAccessState}
                />

                <HomeActionCard
                  to="/request/knowledge"
                  label="ナレッジライブラリ"
                  title="資料を使う"
                  description="PDFやWeb、社長メモを登録して、AIで繰り返し使う。"
                  accessState={knowledgeAccessState}
                />
              </section>

              <section aria-labelledby="home-other-work-heading" className="w-full rounded-[1.3rem] border border-[#EADBC8] bg-white/30 p-3">
                <h2 id="home-other-work-heading" className="text-sm font-black text-[#7C5A4E]">
                  その他の仕事
                </h2>
                <div className="mt-2 space-y-1.5">
                  <HomeWorkAction
                    title="社長メモ"
                    accessState={memoAccessState}
                    onClick={() => navigate("/request/executive-memos", { state: { returnTo: "/home" } })}
                  />
                  <HomeWorkAction
                    title="領収書整理"
                    accessState={receiptAccessState}
                    onClick={() => navigate("/receipt", { state: { returnTo: "/home" } })}
                  />
                </div>
              </section>
            </>
          ) : null}
        </main>

        <footer className="w-full py-8 text-center opacity-30">
          <AppVersionText className="font-mono text-[10px] uppercase tracking-widest" />
        </footer>
      </div>


      {isPrivilegeModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="privilege-code-dialog-title"
            className="w-full max-w-sm rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <h2 id="privilege-code-dialog-title" className="text-base font-black text-[#5D4D46]">
              特典コードを入力
            </h2>
            {privilegeApplySuccess ? (
              <>
                <p className="mt-3 rounded-xl bg-[#F1FFF5] px-3 py-3 text-sm font-bold leading-6 text-[#2D7A4F]">
                  特典コードを適用しました！
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={closePrivilegeModal}
                    className="w-full rounded-xl bg-[#7C5A4E] px-3 py-3 text-xs font-black text-white transition hover:bg-[#6A4D43]"
                  >
                    閉じる
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm font-medium leading-6 text-[#7B6861]">
                  特典コードを入力して追加特典を受け取ります。
                </p>
                <form className="mt-5" onSubmit={handlePrivilegeApply}>
                  <label htmlFor="home-privilege-code" className="sr-only">
                    特典コード
                  </label>
                  <input
                    id="home-privilege-code"
                    value={privilegeCode}
                    onChange={(event) => {
                      setPrivilegeCode(event.target.value);
                      setPrivilegeApplyError(null);
                    }}
                    autoComplete="off"
                    className="w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-sm font-bold text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#EADBC8]"
                    placeholder="特典コードを入力"
                  />
                  {privilegeApplyError ? (
                    <p role="alert" className="mt-3 rounded-xl bg-[#FFF3F1] px-3 py-3 text-xs font-bold leading-5 text-[#A1493E]">
                      {privilegeApplyError}
                    </p>
                  ) : null}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                      type="submit"
                      disabled={privilegeApplyLoading}
                      className="rounded-xl bg-[#7C5A4E] px-3 py-3 text-xs font-black text-white transition hover:bg-[#6A4D43] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {privilegeApplyLoading ? "適用しています..." : "特典コードを適用する"}
                    </button>
                    <button
                      type="button"
                      disabled={privilegeApplyLoading}
                      onClick={closePrivilegeModal}
                      className="rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FDF8F5] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      キャンセル
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      ) : null}

      {isUsageModalOpen && accountState?.authStatus === "authenticated" ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="usage-summary-dialog-title"
            className="max-h-full w-full max-w-sm overflow-y-auto rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <UsageSummary accountState={accountState} rows={usageRows} titleId="usage-summary-dialog-title" />
            <button
              type="button"
              onClick={closeUsageModal}
              className="mt-4 w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FDF8F5]"
            >
              閉じる
            </button>
          </div>
        </div>
      ) : null}

      {isKnowledgeSettingsOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-settings-dialog-title"
            className="max-h-full w-full max-w-md overflow-y-auto rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <div className="flex items-center justify-between gap-3">
              <h2 id="knowledge-settings-dialog-title" className="text-base font-black text-[#5D4D46]">
                ナレッジ設定
              </h2>
              <button
                type="button"
                aria-label="ナレッジ設定を閉じる"
                onClick={() => setIsKnowledgeSettingsOpen(false)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D9C4AF] bg-white text-lg font-black text-[#7C5A4E]"
              >
                ×
              </button>
            </div>
            <div className="mt-4">
              <KnowledgeAutoRegistrationSettings />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
